import { useNavigate, useParams } from 'react-router-dom';

import { useAudienceUpdateMutation } from '@occc-app/dataAccessLayer/audience/mutations';
import { Audience_Status, AudienceUpdateMutation } from '@occc/graphql';
import { routeCreator } from '@occc-app/routes/Audience/utils';
import { useChainId } from '@occc-app/Chain/hooks';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { AudienceUpdatePage } from './AudienceUpdatePage';
import { prepareAudienceUpdateInput } from './utils/prepareAudienceUpdateInput';

import { getFormInitialValues } from '../utils/getFormInitialValues';
import { AudienceFormValues } from '../types';
import { useOpenAndScrollToInvalidSection, useValidateAudience } from '../hooks';
import { useGetAudienceWithDataById } from '../../../dataAccessLayer/audience/useGetAudienceWithDataById';

export const AudienceUpdatePageContainer = () => {
  const navigate = useNavigate();
  const { id: audienceId = '' } = useParams<{ id: string }>();
  const chainId = useChainId();
  const validateAudience = useValidateAudience();
  const { loading, error, data, loaded } = useGetAudienceWithDataById(audienceId, chainId);
  const initialValues = getFormInitialValues(data);
  const isUserPermittedToEditAudience = useIsUserPermittedTo(Feature.AUDIENCE_EDIT);
  const isArchived = data?.status === Audience_Status.Archived;
  const isActive = data?.status === Audience_Status.Active;
  const isEditable = isUserPermittedToEditAudience && !isArchived && !isActive;

  const onCompleted = (mutation: AudienceUpdateMutation) =>
    navigate(routeCreator.details(mutation.audienceUpdate.uuid));

  const [audienceUpdateMutation] = useAudienceUpdateMutation({ onCompleted });
  const openAndScrollToInvalidSection = useOpenAndScrollToInvalidSection();

  const audienceUpdate = (values: AudienceFormValues) => {
    const input = prepareAudienceUpdateInput(audienceId, values);
    return audienceUpdateMutation({ variables: { input } });
  };

  return (
    <AudienceUpdatePage
      loading={loading}
      loaded={loaded}
      error={error}
      audienceId={audienceId}
      initialValues={initialValues}
      audienceUpdate={audienceUpdate}
      validate={validateAudience}
      openAndScrollToInvalidSection={openAndScrollToInvalidSection}
      isUserPermittedToEditAudience={isEditable}
    />
  );
};
